import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import {
  useChecklistTemplates,
  useDeleteChecklistTemplate,
  type ChecklistTemplate,
} from '@/hooks/useChecklists';
import { useNotify } from '@/stores/notificationStore';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { describeApiError } from '@/lib/describeApiError';

interface Props {
  onEdit: (template: ChecklistTemplate) => void;
  includeInactive: boolean;
}

export function TemplatesList({ onEdit, includeInactive }: Props) {
  const { t } = useTranslation('checklists');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const { data: templates, isLoading } = useChecklistTemplates({ includeInactive });
  const deleteTemplate = useDeleteChecklistTemplate();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTemplate, setMenuTemplate] = useState<ChecklistTemplate | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ChecklistTemplate | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, template: ChecklistTemplate) => {
    setMenuAnchor(event.currentTarget);
    setMenuTemplate(template);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuTemplate(null);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteTemplate.mutateAsync(confirmDelete.id);
      notify.success(t('template.deleted'));
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.templateDeleteFailed') }));
    }
    setConfirmDelete(null);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!templates || templates.length === 0) {
    return (
      <Alert severity="info">
        {t('template.noTemplates')}
        {/*
          The hint was translated in both locales but never rendered, so the
          empty state named the problem without saying what to do (#2622).
        */}
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {t('template.noTemplatesHint')}
        </Typography>
      </Alert>
    );
  }

  return (
    <>
      <Stack spacing={2}>
        {templates.map((template) => (
          <Card key={template.id} variant="outlined" sx={{ opacity: template.isActive ? 1 : 0.6 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box flex={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {template.name}
                </Typography>
                {template.description && (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {template.description}
                  </Typography>
                )}
                <Stack direction="row" spacing={1} mt={0.5}>
                  <Chip
                    size="small"
                    label={t('template.itemCount', { count: (template.items as unknown[])?.length ?? 0 })}
                    variant="outlined"
                  />
                  {template.isDefault && (
                    <Chip size="small" label={t('template.isDefault')} color="primary" variant="outlined" />
                  )}
                  {!template.isActive && (
                    <Chip size="small" label={tc('inactive')} color="default" />
                  )}
                </Stack>
              </Box>
              <Tooltip title={tc('actions')}>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, template)}
                  aria-label={tc('actions')}
                >
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            if (menuTemplate) onEdit(menuTemplate);
            handleMenuClose();
          }}
        >
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{tc('edit')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setConfirmDelete(menuTemplate);
            handleMenuClose();
          }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>{tc('delete')}</ListItemText>
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={!!confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => void handleDelete()}
        title={tc('delete')}
        message={t('template.deleteConfirm', { name: confirmDelete?.name ?? '' })}
        confirmColor="error"
      />
    </>
  );
}
